/// <amd-module name="Test/Analytics/TestResultsGrid/Components/TestResultsTreeGrid" />
import { ColumnActionsMode, IColumn, SelectionMode } from "OfficeFabric/DetailsList";
import * as React from "react";
import { ColumnIndices, IDetailedListColumn, IDetailedListItem, SortOrder, TestOutcome, TreeNodeType } from "Test/Analytics/Common/Types";
import * as CommonTypes from "Test/Analytics/Common/Types";
import * as TcmTelemetryConstants from "Test/Analytics/Common/Utils/TcmTelemetryConstants";
import { publishEvent } from "Test/Analytics/Common/Utils/TelemetryService";
import { isShowFlakyTestsDetailsEnabled } from "Test/Analytics/Common/Utils/FeatureFlagUtils";
import { TestResultsGridActionsCreator } from "Test/Analytics/TestResultsGrid/Actions/TestResultsGridActionsCreator";
import * as Resources from "Test/Analytics/TestResultsGrid/Resources";
import { DetailedTestListStore, IDetailedTestListState } from "Test/Analytics/TestResultsGrid/Store/TestResultsTreeStore";
import { ClickableLabel } from "Test/Analytics/TestResultsGridCommon/Components/ClickableLabel";
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { Components, css, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ITreeItem, ITreeListItem, Tree } from "VSSUI/Tree";

export interface ITestResultsListProps extends IVssComponentProperties {
    ariaLabel?: string;
    ariaLabelForGrid?:string;
    testReportContext: CommonTypes.ITestReportContext;
    store: DetailedTestListStore;
    actionCreator: TestResultsGridActionsCreator;
    onSetMinWidth: (minWidth: number) => void;
    onItemInvoked: (groupItem: CommonTypes.IDetailedListItem) => void;
}

interface IExtendedIDetailedTestListState extends IDetailedTestListState {
    minWidth?: number;
}

export class TestResultsTree extends VssComponent<ITestResultsListProps, IExtendedIDetailedTestListState, IVssComponentContext> {
    private minWidth: number = 1100;
    private columnWidthOffset: number = 8;

    public constructor(props: ITestResultsListProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            sortedColumn: this.props.store.getState().sortedColumn,
            gridViewDisplayType: this.props.store.getState().gridViewDisplayType,
            totalTestResults: null,
            treeItemProvider: this.props.store.getTreeItemProvider(),
            items: []
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.props.store.addChangedListener(this._onStoreUpdate);
    }

    public componentWillUnmount(): void {
        this.props.store.removeChangedListener(this._onStoreUpdate);
        super.componentWillUnmount();
    }

    public componentDidUpdate(): void {
        if (this.props && this.props.onSetMinWidth && this.state.minWidth !== this.minWidth) {
            this.props.onSetMinWidth(this.minWidth);
        }
    }

    public render(): JSX.Element {
        if (this.state.gridViewDisplayType === CommonTypes.GridViewDisplayType.Loading) {
            return <Spinner className="analytics-card-spinner flex-grow flex-column" size={SpinnerSize.large} />;
        } else {
            return (
                <Tree
                    className={css("results-tree", this.props.className)}
                    columns={this._getColumns()}
                    onItemInvoked={this.onTreeItemInvoked}
                    treeItemProvider={this.state.treeItemProvider}
                    selectionMode={SelectionMode.single}
                    ariaLabel={this.props.ariaLabel}
                    ariaLabelForGrid={this.props.ariaLabelForGrid}
                />
            );
        }
    }

    public _onColumnOrderChanged(orderedColumn: CommonTypes.IDetailedListColumn) {
        this.props.actionCreator.detailedTestListColumnOrderChanged(orderedColumn);

        let message = Utils_String.localeFormat(Resources.DetailedListSortedDesc, CommonTypes.ColumnIndices[orderedColumn.column]);
        if (orderedColumn.sortOrder == SortOrder.Ascending) {
            message = Utils_String.localeFormat(Resources.DetailedListSortedAsc, CommonTypes.ColumnIndices[orderedColumn.column]);
        }
        const confValues = this.props.store.getConfValue();
        if (this.props.store.isListCompletelyFetched()) {
            this.props.actionCreator.detailedTestListSortByColumn();
            announce(message, true, 500);
        } else {
            // invoke action to start fetching new data for list ordered by this ordered column.
            this.props.actionCreator
                .updateDetailedTestList(
                    this.props.testReportContext,
                    confValues,
                    orderedColumn,
                    this.props.store.getNextPageToken(undefined),
                    true,
                    true
                )
                .then(() => {
                    announce(message, true, 500);
                });
        }

        const telemetryData: { [key: string]: string | number } = {
            [TcmTelemetryConstants.SortBy]: CommonTypes.ColumnIndices[orderedColumn.column].toString(),
            [TcmTelemetryConstants.GroupBy]: CommonTypes.GroupBy[confValues.groupBy].toString(),
            [TcmTelemetryConstants.Duration]: CommonTypes.Period[confValues.period].toString()
        };
        publishEvent(
            this.context.pageContext,
            TcmTelemetryConstants.featureTestAX_ResultsGrid,
            telemetryData,
            this.props.testReportContext,
            confValues
        );
    }

    private _onDetailedListShowMore = (item: CommonTypes.IDetailedListItem) => {
        const confValues = this.props.store.getConfValue();
        if (item.depth) {
            /* For items inside group */
            this.props.actionCreator.detailedListShowMoreInsideGroup(
                this.props.testReportContext,
                confValues,
                this.props.store.getState().sortedColumn,
                this.props.store.getNextPageToken(item.parentItemKey),
                item.parentItemKey ? item.parentItemKey : 0
            );
        } else {
            this.props.actionCreator.detailedListShowMore(
                this.props.testReportContext,
                confValues,
                this.props.store.getState().sortedColumn,
                this.props.store.getNextPageToken(item.parentItemKey)
            );
        }
    };

    private onTreeItemInvoked = (treeListItem: ITreeListItem<CommonTypes.IDetailedListItem>) => {
        if (!treeListItem.treeItem || !treeListItem.treeItem.item) {
            return;
        } else {
            this.props.onItemInvoked(treeListItem.treeItem.item);
            publishEvent(
                this.context.pageContext,
                TcmTelemetryConstants.featureTestAX_ViewTest,
                {},
                this.props.testReportContext,
                this.props.store.getConfValue()
            );
        }
    };

    private _getColumns(): IColumn[] {
        // Initializing first (Test Name Column)
        let chartColumns: IColumn[] = [
            {
                fieldName: Resources.ResultGridTitle_Test,
                key: ColumnIndices.Test.toString(),
                name: Resources.ResultGridTitle_Test,
                ariaLabel: Resources.ResultGridTitle_Test,
                minWidth: 300,
                isResizable: true,
                columnActionsMode: ColumnActionsMode.disabled,
                isSorted: false,
                onRender: (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    let onClickMethod;
                    if (treeItem && treeItem.item) {
                        const item = treeItem.item;
                        switch (item.nodeType) {
                            case TreeNodeType.leaf:
                                onClickMethod = () => {
                                    this.props.onItemInvoked(item);
                                };
                                break;

                            case TreeNodeType.showMore:
                                onClickMethod = () => {
                                    this._onDetailedListShowMore(item);
                                };
                                break;

                            default:
                                onClickMethod = undefined;
                        }

                        return (
                            <ClickableLabel
                                value={item.itemName}
                                onClick={onClickMethod}
                                showLoadingOnClick={item.nodeType === TreeNodeType.showMore}
                                ariaLabel={Utils_String.localeFormat(Resources.AriaLabelLinkFormat, item.itemName, Resources.ResultGridTitle_Test)}
                            />
                        );
                    }
                    return "";
                }
            }
        ];

        // Pushing chosen outcome columns
        const confValue = this.props.store.getConfValue();
        if (confValue && confValue.outcomes && confValue.outcomes.length > 0) {
            for (const outcome of confValue.outcomes.sort()) {
                chartColumns.push(this._getColumnPropsForAnOutcome(outcome));
            }
        }

        // Concatenating fixed columns
        chartColumns = chartColumns.concat([
            {
                fieldName: Resources.PassRate,
                key: ColumnIndices.Passrate.toString(),
                name: Resources.PassRate,
                ariaLabel: Resources.PassRate,
                minWidth: 100,
                isResizable: true,
                isSorted: this.state.sortedColumn.column === ColumnIndices.Passrate,
                isSortedDescending: this.state.sortedColumn.sortOrder === SortOrder.Descending,
                onColumnClick: this._onColumnClicked,
                onRender: (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    return this._getColumnDisplayNode(treeItem.item ? treeItem.item.passPercentage : undefined, Resources.PassRate);
                }
            },
            {
                fieldName: Resources.TotalCountText,
                key: ColumnIndices.TotalCount.toString(),
                name: Resources.TotalCountText,
                ariaLabel: Resources.TotalCountText,
                minWidth: 100,
                isResizable: true,
                isSorted: this.state.sortedColumn.column === ColumnIndices.TotalCount,
                isSortedDescending: this.state.sortedColumn.sortOrder === SortOrder.Descending,
                onColumnClick: this._onColumnClicked,
                onRender: (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    return this._getColumnDisplayNode(treeItem.item ? treeItem.item.totalCount : undefined, Resources.TotalCountText);
                }
            },
            {
                fieldName: Resources.AverageDurationText,
                key: ColumnIndices.AvgDuration.toString(),
                name: Resources.AverageDurationText,
                ariaLabel: Resources.AverageDurationText,
                minWidth: isShowFlakyTestsDetailsEnabled(this.context.pageContext) ? 100 : 200,
                isResizable: true,
                isSorted: this.state.sortedColumn.column === ColumnIndices.AvgDuration,
                isSortedDescending: this.state.sortedColumn.sortOrder === SortOrder.Descending,
                onColumnClick: this._onColumnClicked,
                onRender: (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    return treeItem.item ? (
                        <span
                            aria-label={Utils_String.localeFormat(
                                Resources.AriaLabelFormat,
                                treeItem.item.avgDurationAriaLabel,
                                Resources.AverageDurationText
                            )}
                        >
                            {treeItem.item.avgDuration}
                        </span>
                    ) : null;
                }
            }
        ]);

        if (isShowFlakyTestsDetailsEnabled(this.context.pageContext)) {
            chartColumns.push({
                fieldName: Resources.FlakyText,
                key: ColumnIndices.FlakyCount.toString(),
                name: Resources.FlakyText,
                ariaLabel: Resources.FlakyText,
                minWidth: 200,
                isResizable: true,
                isSorted: this.state.sortedColumn.column == ColumnIndices.FlakyCount,
                isSortedDescending: this.state.sortedColumn.sortOrder == SortOrder.Descending,
                onColumnClick: this._onColumnClicked,
                onRender: (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    return treeItem.item ? (
                        <span
                            arial-label={Utils_String.localeFormat(
                                Resources.AriaLabelFormat,
                                treeItem.item.flakyCount,
                                Resources.FlakyText
                            )}
                        >
                            {treeItem.item.flakyCount || 0}
                        </span>
                    ) : null;
                }
            });
        }

        let minWidth = 0;
        chartColumns.forEach(column => {
            minWidth += column.minWidth + this.columnWidthOffset;
        });

        this.minWidth = Math.max(this.minWidth, minWidth);

        return chartColumns;
    }

    private _getColumnPropsForAnOutcome(outcome: TestOutcome): IColumn {
        let fieldName = "";
        let index: ColumnIndices = 0;
        let onRender = (item?: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {};

        switch (outcome) {
            case TestOutcome.Failed:
                fieldName = Resources.TestOutcome_Failed;
                index = ColumnIndices.FailedCount;
                onRender = (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    return this._getColumnDisplayNode(treeItem.item ? treeItem.item.failedCount : undefined, Resources.TestOutcome_Failed);
                };
                break;

            case TestOutcome.Passed:
                fieldName = Resources.TestOutcome_Passed;
                index = ColumnIndices.PassedCount;
                onRender = (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    return this._getColumnDisplayNode(treeItem.item ? treeItem.item.passedCount : undefined, Resources.TestOutcome_Passed);
                };
                break;

            case TestOutcome.Inconclusive:
                fieldName = Resources.TestOutcome_Inconclusive;
                index = ColumnIndices.InconclusiveCount;
                onRender = (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    return this._getColumnDisplayNode(
                        treeItem.item ? treeItem.item.inconclusiveCount : undefined,
                        Resources.TestOutcome_Inconclusive
                    );
                };
                break;

            case TestOutcome.Aborted:
                fieldName = Resources.TestOutcome_Aborted;
                index = ColumnIndices.AbortedCount;
                onRender = (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    return this._getColumnDisplayNode(treeItem.item ? treeItem.item.abortedCount : undefined, Resources.TestOutcome_Aborted);
                };
                break;

            case TestOutcome.NotExecuted:
                fieldName = Resources.TestOutcome_NotExecuted;
                index = ColumnIndices.NotExecutedCount;
                onRender = (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    return this._getColumnDisplayNode(treeItem.item ? treeItem.item.notExecutedCount : undefined, Resources.TestOutcome_NotExecuted);
                };
                break;

            case TestOutcome.Error:
                fieldName = Resources.TestOutcome_Error;
                index = ColumnIndices.ErrorCount;
                onRender = (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    return this._getColumnDisplayNode(treeItem.item ? treeItem.item.errorCount : undefined, Resources.TestOutcome_Error);
                };
                break;

            case TestOutcome.NotImpacted:
                fieldName = Resources.TestOutcome_NotImpacted;
                index = ColumnIndices.NotImpactedCount;
                onRender = (treeItem: ITreeItem<IDetailedListItem>, index?: number, column?: IColumn) => {
                    return this._getColumnDisplayNode(treeItem.item ? treeItem.item.notImpactedCount : undefined, Resources.TestOutcome_NotImpacted);
                };
                break;
        }

        return {
            fieldName: fieldName,
            key: index.toString(),
            name: fieldName,
            ariaLabel: fieldName,
            minWidth: 100,
            isResizable: true,
            isSorted: this.state.sortedColumn.column === index,
            isSortedDescending: this.state.sortedColumn.sortOrder === SortOrder.Descending,
            onColumnClick: this._onColumnClicked,
            onRender: onRender
        } as IColumn;
    }

    private _getColumnDisplayNode(text: string | number | undefined, columnHeader?: string): JSX.Element {
        const ariaLabel = text ? Utils_String.localeFormat(Resources.AriaLabelFormat, text, columnHeader) : "";
        return <span aria-label={ariaLabel}> {text} </span>;
    }

    private _onColumnClicked = (ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
        const orderedColumn: IDetailedListColumn = {} as IDetailedListColumn;
        if (column) {
            if (column.key === this.state.sortedColumn.column.toString()) {
                orderedColumn.column = this.state.sortedColumn.column;
                orderedColumn.sortOrder = this.state.sortedColumn.sortOrder === SortOrder.Ascending ? SortOrder.Descending : SortOrder.Ascending;
            } else {
                orderedColumn.column = Number(column.key);
                orderedColumn.sortOrder = SortOrder.Descending; // Default we are keeping as descending.
            }
        }

        if (orderedColumn.column && orderedColumn.sortOrder) {
            this._onColumnOrderChanged(orderedColumn);
        }
    };

    private _onStoreUpdate = () => {
        this.setState({
            sortedColumn: this.props.store.getState().sortedColumn,
            gridViewDisplayType: this.props.store.getState().gridViewDisplayType,
            totalTestResults: null,
            treeItemProvider: this.props.store.getTreeItemProvider(),
            items: []
        });
    };
}
Components.add("TestResultsTree", TestResultsTree);
